import React, { useState, useRef } from "react";
import { Plus, Image, X, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { CreatePostData } from "@/types/community";
import { uploadCommunityPostImage } from "@/services/imageUploadService";
import { useAuth } from "@/hooks/useAuth";
import RichTextEditor from "@/components/ui/RichTextEditor";

interface CreatePostModalProps {
  communityId: string;
  communityName: string;
  onPostCreated: () => void;
  onCreatePost: (data: CreatePostData) => Promise<void>;
  isCreating: boolean;
}

export const CreatePostModal: React.FC<CreatePostModalProps> = ({
  communityId,
  communityName,
  onPostCreated,
  onCreatePost,
  isCreating
}) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image_url: ''
  });
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  // Keys para localStorage
  const STORAGE_KEY = `create_post_draft_${communityId}`;
  const MODAL_STATE_KEY = `create_post_modal_open_${communityId}`;

  // Carregar draft e estado do modal ao montar componente
  React.useEffect(() => {
    const savedDraft = localStorage.getItem(STORAGE_KEY);
    const savedModalState = localStorage.getItem(MODAL_STATE_KEY);
    
    if (savedModalState === 'true') {
      setOpen(true);
    }
    
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        setFormData(draft);
      } catch (error) {
        console.error('Erro ao carregar draft:', error);
      }
    }
  }, [STORAGE_KEY, MODAL_STATE_KEY]);

  // Salvar estado do modal no localStorage
  React.useEffect(() => {
    if (open) {
      localStorage.setItem(MODAL_STATE_KEY, 'true');
    } else {
      localStorage.removeItem(MODAL_STATE_KEY);
      // Só remove o draft se o modal foi fechado intencionalmente
      if (!formData.title.trim() && !formData.content.trim() && !formData.image_url.trim()) {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, [open, MODAL_STATE_KEY, STORAGE_KEY, formData]);

  // Salvar draft sempre que formData mudar
  React.useEffect(() => {
    if (formData.title.trim() || formData.content.trim() || formData.image_url.trim()) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    }
  }, [formData, STORAGE_KEY]);

  // Manter modal aberto quando página perde/ganha foco
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      const savedModalState = localStorage.getItem(MODAL_STATE_KEY);
      if (savedModalState === 'true' && !open) {
        // Reabrir modal se deveria estar aberto
        setTimeout(() => {
          setOpen(true);
        }, 100);
      }
    };

    const handleFocus = () => {
      const savedModalState = localStorage.getItem(MODAL_STATE_KEY);
      if (savedModalState === 'true' && !open) {
        setTimeout(() => {
          setOpen(true);
        }, 100);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [open, MODAL_STATE_KEY]);

  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (open && (formData.title.trim() || formData.content.trim() || formData.image_url.trim())) {
        e.preventDefault();
        e.returnValue = 'Você tem alterações não salvas. Tem certeza que deseja sair?';
        return e.returnValue;
      }
    };

    if (open) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [open, formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim()) {
      toast({
        title: "Campos obrigatórios",
        description: "Título e conteúdo são obrigatórios.",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log('📝 [CreatePostModal] Dados do formulário:', formData);
      
      const postData: CreatePostData = {
        community_id: communityId,
        title: formData.title.trim(),
        content: formData.content.trim(),
        image_url: formData.image_url.trim() || undefined
      };

      console.log('📝 [CreatePostModal] Dados do post a ser criado:', postData);

      await onCreatePost(postData);
      
      // Limpar formulário, draft e fechar modal
      setFormData({ title: '', content: '', image_url: '' });
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(MODAL_STATE_KEY);
      setOpen(false);
      onPostCreated();
      
    } catch (error) {
      // Erro já tratado no componente pai
    }
  };



  const handleImageUpload = async (file: File) => {
    if (!user?.id) {
      toast({
        title: "Erro de autenticação",
        description: "Usuário não encontrado.",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingImage(true);
    
    try {
      const result = await uploadCommunityPostImage(file, user.id);
      
      if (result.success && result.url) {
        setFormData(prev => ({ ...prev, image_url: result.url! }));
        toast({
          title: "Imagem enviada!",
          description: "A imagem foi hospedada em nossos servidores com sucesso.",
        });
      } else {
        toast({
          title: "Erro no upload",
          description: result.error || "Erro desconhecido ao fazer upload da imagem.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro no upload",
        description: "Erro inesperado ao fazer upload da imagem.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
    // Limpar o input para permitir upload do mesmo arquivo novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Função para cancelar explicitamente - usada apenas pelos botões
  const handleCancel = () => {
    // Se há conteúdo, confirmar
    if (formData.title.trim() || formData.content.trim() || formData.image_url.trim()) {
      const confirm = window.confirm('Você tem alterações não salvas. Tem certeza que deseja fechar?');
      if (!confirm) return;
    }
    
    // Limpar tudo ao fechar intencionalmente
    setFormData({ title: '', content: '', image_url: '' });
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(MODAL_STATE_KEY);
    setOpen(false);
  };

  return (
    <Dialog 
      open={open} 
      onOpenChange={() => {}}
      modal={true}
    >
      <DialogTrigger asChild>
        <Button className="bg-[#00B6F0] hover:bg-[#0095CC] text-black">
          <Plus size={16} className="mr-2" />
          Criar Post
        </Button>
      </DialogTrigger>
      
      <DialogContent 
        className="bg-[#1A2333] border-[#222A35] text-white max-w-2xl max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-xl">Criar Novo Post</DialogTitle>
          <DialogDescription className="text-gray-400">
            Compartilhe algo interessante com a comunidade <span className="font-medium text-[#00B6F0]">{communityName}</span>
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Título */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-sm font-medium text-gray-300">
              Título do Post *
            </Label>
            <Input
              id="title"
              placeholder="Digite um título chamativo..."
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="bg-[#242C37] border-[#333A45] text-white placeholder-gray-400 focus:border-[#00B6F0]"
              maxLength={200}
              required
            />
            <div className="text-xs text-gray-400 text-right">
              {formData.title.length}/200
            </div>
          </div>
          
          {/* Conteúdo */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-300">
              Conteúdo *
            </Label>
            <RichTextEditor
              value={formData.content}
              onChange={(content) => setFormData(prev => ({ ...prev, content }))}
              placeholder="O que você gostaria de compartilhar com a comunidade?"
              maxLength={5000}
              showPreview={true}
              disabled={isCreating}
            />
          </div>
          
          {/* Imagem */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-300 flex items-center gap-2">
              <Image size={16} />
              Imagem (Opcional)
            </Label>
            
            {/* Botão de Upload */}
            <div className="space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleFileInputChange}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                className="w-full border-[#333A45] bg-[#242C37] hover:bg-[#2A3441] text-gray-300"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingImage || isCreating}
              >
                {isUploadingImage ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-300 mr-2"></div>
                    Enviando imagem...
                  </>
                ) : (
                  <>
                    <Upload size={16} className="mr-2" />
                    Hospedar Imagem em Nossos Servidores
                  </>
                )}
              </Button>
              <div className="text-xs text-gray-400 text-center">
                ou cole uma URL de imagem abaixo
              </div>
            </div>
            
            {/* Campo de URL */}
            <Input
              placeholder="Cole a URL de uma imagem..."
              value={formData.image_url}
              onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
              className="bg-[#242C37] border-[#333A45] text-white placeholder-gray-400 focus:border-[#00B6F0]"
              type="url"
            />
            
            {/* Preview da imagem */}
            {formData.image_url && (
              <div className="relative">
                <img
                  src={formData.image_url}
                  alt="Preview"
                  className="w-full max-h-48 object-cover rounded-lg"
                  onError={() => {
                    toast({
                      title: "Imagem inválida",
                      description: "A URL da imagem não é válida.",
                      variant: "destructive",
                    });
                  }}
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                >
                  <X size={14} />
                </Button>
              </div>
            )}
            
            <div className="text-xs text-gray-400">
              Aceita imagens até 5MB (.jpg, .png, .gif, .webp). Imagens hospedadas em nossos servidores ficam sempre disponíveis.
            </div>
          </div>
          
          {/* Ações */}
          <div className="flex gap-3 pt-4 border-t border-[#333A45]">
            <Button
              type="submit"
              disabled={isCreating || !formData.title.trim() || !formData.content.trim()}
              className="flex-1 bg-[#00B6F0] hover:bg-[#0095CC] text-black font-medium"
            >
              {isCreating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                  Publicando...
                </>
              ) : (
                <>
                  <Plus size={16} className="mr-2" />
                  Publicar Post
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isCreating}
              className="border-[#333A45] text-gray-300 hover:bg-[#242C37] hover:text-white"
            >
              Cancelar
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}; 